import { Box, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';

import { WhatsAppLTOComponent } from 'domains/content/types';

export type Props = {
    lto: WhatsAppLTOComponent['limited_time_offer'] | null;
    onChange: (lto: WhatsAppLTOComponent['limited_time_offer']) => void;
};

const DEFAULT_LTO: WhatsAppLTOComponent['limited_time_offer'] = {
    text: '',
    has_expiration: false,
};

const WhatsAppLTOSection = ({ lto, onChange }: Props) => {
    const current = lto ?? DEFAULT_LTO;

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ ...current, text: e.target.value });
    };

    const handleExpirationToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...current,
            has_expiration: e.target.checked,
            expiration_time_ms: e.target.checked ? DateTime.now().plus({ days: 7 }).toMillis() : undefined,
        });
    };

    const handleDateChange = (value: DateTime | null) => {
        if (!value) return;
        onChange({ ...current, expiration_time_ms: value.toMillis() });
    };

    const expiryDateTime = current.expiration_time_ms ? DateTime.fromMillis(current.expiration_time_ms) : null;

    return (
        <Stack spacing={2} data-testid="whatsapp-lto-section">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" fontWeight={600}>
                    Limited-Time Offer
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Marketing templates only
                </Typography>
            </Stack>

            <TextField
                data-testid="lto-title-input"
                label="Offer title"
                value={current.text}
                onChange={handleTitleChange}
                size="small"
                fullWidth
                placeholder="e.g. Exclusive 20% off — today only"
                inputProps={{ 'aria-label': 'Offer title' }}
            />

            <FormControlLabel
                control={
                    <Switch
                        data-testid="lto-expiration-toggle"
                        checked={current.has_expiration}
                        onChange={handleExpirationToggle}
                        inputProps={{ 'aria-label': 'Enable expiry countdown' }}
                    />
                }
                label="Show expiry countdown timer"
            />

            {current.has_expiration && (
                <Box data-testid="lto-datetime-picker">
                    <DateTimePicker
                        label="Offer expires at"
                        value={expiryDateTime}
                        onChange={handleDateChange}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                inputProps: { 'aria-label': 'Offer expiry date and time' },
                            },
                        }}
                    />
                </Box>
            )}

            <Typography variant="caption" color="text.secondary">
                The countdown timer is displayed live in WhatsApp and updates in real time until the offer expires.
            </Typography>
        </Stack>
    );
};

export default WhatsAppLTOSection;
